import React, { FC, MouseEventHandler, ReactElement } from 'react'
import clsx from 'clsx'
import Toolbar from '@material-ui/core/Toolbar'
import { Button } from '@material-ui/core'
import Icon, { IconName } from '../../../../React/Common/Components/Icon'
import { usePermissions, UserPermissions } from '../../../Hooks/usePermissions'
import { AdminBackendPermissionNames } from '../../../../React/Common/Constants/Permissions'

export interface DeleteAddToolbarLocaleText {
    deleteButton: string
    addButton: string
    changeButton: string
    viewButton: string
    selectionActive: (count: number) => React.ReactNode
}

export const DeleteAddToolbarLocaleTextDefaults: DeleteAddToolbarLocaleText = {
    deleteButton: 'Delete',
    addButton: 'Add',
    changeButton: 'Change',
    viewButton: 'View',
    selectionActive: (count: number): string => `${count} Selected`
}

interface DeleteAddToolbarProps {
    itemsSelectedCount: number
    localeText: DeleteAddToolbarLocaleText
    onAddClick?: MouseEventHandler<HTMLButtonElement>
    onDeleteClick?: MouseEventHandler<HTMLButtonElement>
    onChangeClick?: MouseEventHandler<HTMLButtonElement>
    onViewClick?: MouseEventHandler<HTMLButtonElement>
    permissionName?: AdminBackendPermissionNames
}

/**
 * DeleteAddToolbar component.
 *
 * @param {DeleteAddToolbarProps} props - Component properties.
 * @returns {ReactElement} The rendered component.
 */
export const DeleteAddToolbar: FC<DeleteAddToolbarProps> = ({
    itemsSelectedCount,
    onAddClick,
    onDeleteClick,
    localeText,
    onChangeClick,
    onViewClick,
    permissionName
}: DeleteAddToolbarProps): ReactElement => {
    const userPermissions: UserPermissions = usePermissions(permissionName)

    return (
        <div className={clsx('delete-add-toolbar', itemsSelectedCount > 0 && 'delete-add-toolbar-has-selection')}>
            <Toolbar>
                {itemsSelectedCount > 0 ? (
                    <>
                        <b>{localeText.selectionActive(itemsSelectedCount)}</b>
                        <div className='delete-change-buttons-container'>
                            {userPermissions?.Delete ? (
                                <Button
                                    variant='contained'
                                    color='default'
                                    startIcon={<Icon iconName={IconName.DELETE} />}
                                    onClick={onDeleteClick}
                                >
                                    {localeText.deleteButton}
                                </Button>
                            ) : (
                                <></>
                            )}
                            {itemsSelectedCount === 1 && userPermissions?.Update ? (
                                <Button
                                    variant='contained'
                                    color='default'
                                    startIcon={<Icon iconName={IconName.CHANGE} />}
                                    onClick={onChangeClick}
                                >
                                    {localeText.changeButton}
                                </Button>
                            ) : (
                                <></>
                            )}
                            {itemsSelectedCount === 1 && userPermissions?.Read && !userPermissions?.Update ? (
                                <Button
                                    variant='contained'
                                    color='default'
                                    startIcon={<Icon iconName={IconName.PREVIEW} />}
                                    onClick={onViewClick}
                                >
                                    {localeText.viewButton}
                                </Button>
                            ) : (
                                <></>
                            )}
                        </div>
                    </>
                ) : (
                    <></>
                )}
                {!itemsSelectedCount && userPermissions?.Create ? (
                    <Button
                        variant='contained'
                        color='primary'
                        startIcon={<Icon iconName={IconName.ADD} />}
                        onClick={onAddClick}
                    >
                        {localeText.addButton}
                    </Button>
                ) : (
                    <></>
                )}
            </Toolbar>
        </div>
    )
}
